import { useState, useEffect, useCallback, useRef } from "react";
import { FaEdit, FaTrash, FaRegCopy, FaDownload, FaUpload } from "react-icons/fa";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import JSZip from "jszip";
import { useDispatch } from 'react-redux';
import { updateJsonData } from '../../../store/jsonSlice';
import { importZipVariant } from "../../../helpers/zipImporter";
import { uploadFile } from "../../../services/api/jsonService.ts";
import axios from 'axios';

const LanguageManageWork = () => {

  // Fonction pour télécharger tout le contenu du dossier de langue (images, sons, json, glb, pdf, etc.) en ZIP côté frontend
  const handleDownloadJson = async (folder) => {
    setDownloadError(null);
    try {
      // 1. Récupérer la liste de tous les fichiers du dossier
      const listResponse = await fetch(`/api/data/${encodeURIComponent(chapterName)}/${encodeURIComponent(folder)}/list-all-files`, { credentials: 'include' });
      if (!listResponse.ok) {
        const err = await listResponse.json().catch(() => ({}));
        throw new Error(err.error || `Status ${listResponse.status}`);
      }
      const files = await listResponse.json(); // tableau de noms de fichiers
      const zip = new JSZip();
      // 2. Télécharger chaque fichier et l'ajouter dans le zip
      await Promise.all(files.map(async (relativePath) => {
        const parts = String(relativePath).split('/').filter(Boolean);
        const encodedPath = parts.map(p => encodeURIComponent(p)).join('/');
        const fileUrl = `/api/data/${encodeURIComponent(chapterName)}/${encodeURIComponent(folder)}/${encodedPath}`;
        const fileResp = await fetch(fileUrl, { credentials: 'include' });
        if (!fileResp.ok) throw new Error(`Erreur lors du téléchargement de ${relativePath}`);

        // Renommer uniquement l'extension du fichier cible pour Unity (.gltf/.glb -> .bytes)
        let finalPathInZip = String(relativePath).trim();
        finalPathInZip = finalPathInZip.replace(/\.(gltf|glb)$/i, '.bytes');

        console.log({
          before: relativePath,
          after: finalPathInZip
        });

        const zipPath = `${folder}/${finalPathInZip}`;

        // Si c'est un fichier JSON, le pretty-print
        if (relativePath.toLowerCase().endsWith('.json')) {
          const text = await fileResp.text();
          const json = JSON.parse(text);
          const prettyJson = JSON.stringify(json, null, 2);
          zip.file(zipPath, prettyJson);
        } else {
          const blob = await fileResp.blob();
          zip.file(zipPath, blob);
        }
      }));
      // 3. Générer le ZIP et déclencher le téléchargement
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `${folder}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
      setDownloadError(err.message);
    }
  };

  const handleImportZip = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.zip';

    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      // Récupérer le nom du dossier depuis le nom du fichier ZIP
      // Nettoyer le suffixe automatique du navigateur ex: "nom (1)" → "nom"
      const sourceFolderName = file.name
        .replace(/\.zip$/i, '')
        .replace(/\s*\(\d+\)\s*$/, '');
      const normalizedSourceFolderName = sourceFolderName.replace(/_export$/i, '');
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);

      if (!window.confirm(`Le fichier ZIP fait ${sizeMB} MB. L'import peut prendre plusieurs minutes. Continuer ?`)) {
        return;
      }

      try {
        console.log('Extraction du ZIP en cours...');
        const zip = new JSZip();
        const zipContent = await zip.loadAsync(file);
        // La variante importée doit toujours atterrir dans le référentiel actuellement ouvert,
        // quel que soit le nom du fichier ZIP.
        const targetChapterName = chapterName;

        const files = [];
        zipContent.forEach((relativePath, zipEntry) => {
          if (!zipEntry.dir) {
            files.push({ path: relativePath, entry: zipEntry });
          }
        });

        console.log(`${files.length} fichiers trouvés dans le ZIP`);

        if (files.length === 0) {
          alert('Le ZIP ne contient aucun fichier.');
          return;
        }

        // Détecter le nom d'origine des fichiers JSON dans le ZIP
        let detectedSourceFolderName = '';
        for (const f of files) {
          const filename = f.path.split('/').pop();
          if (filename.endsWith('.json') && !filename.endsWith('_learn.json') && !filename.startsWith('._')) {
            detectedSourceFolderName = filename.replace(/\.json$/i, '');
            break;
          }
        }
        if (!detectedSourceFolderName) {
          for (const f of files) {
            const filename = f.path.split('/').pop();
            if (filename.endsWith('.json') && !filename.startsWith('._')) {
              detectedSourceFolderName = filename.replace(/(_learn)?\.json$/i, '');
              break;
            }
          }
        }
        const originalVariantName = detectedSourceFolderName || normalizedSourceFolderName;
        console.log(`[ZIP IMPORT] Nom de variante d'origine détecté : "${originalVariantName}"`);

        // Le nom de la variante cible vient du JSON trouvé DANS le zip (le contenu réel),
        // pas du nom du fichier zip lui-même : celui-ci peut être renommé, tronqué par l'OS,
        // ou être le nom du référentiel entier si le mauvais zip a été sélectionné — auquel
        // cas la variante se retrouvait renommée avec le nom du référentiel.
        let targetFolderName = originalVariantName;

        // Si une variante de ce nom existe déjà dans le référentiel courant, proposer un
        // autre nom via la même boîte de dialogue que "Dupliquer la variante".
        if (languageFolders.includes(targetFolderName)) {
          const newName = await askImportVariantName(
            targetFolderName,
            `Une variante nommée "${targetFolderName}" existe déjà dans ce référentiel. Choisissez un autre nom pour l'import.`
          );
          if (newName === null) return; // annulé par l'utilisateur
          targetFolderName = newName; // déjà validé et non-conflictuel par la boîte de dialogue
        }

        // Détecter et ignorer le wrapper du dossier créé par Windows/macOS
        // Exemple: si tous les fichiers commencent par "chapter/", on enlève ce prefix
        let commonPrefix = '';
        if (files.length > 0) {
          const firstPath = files[0].path;
          const firstParts = firstPath.split('/').filter(Boolean);

          // Vérifier si tous les fichiers commencent par le même premier dossier
          if (firstParts.length > 0) {
            const potentialPrefix = firstParts[0];
            const allStartWithPrefix = files.every(f => f.path.startsWith(potentialPrefix + '/'));

            if (allStartWithPrefix) {
              commonPrefix = potentialPrefix + '/';
              console.log(`Dossier wrapper détecté: "${potentialPrefix}", suppression du prefix...`);
            }
          }
        }

        // Créer un indicateur de progression
        let successCount = 0;
        let errorCount = 0;

        // Uploader fichier par fichier en préservant la structure
        for (let i = 0; i < files.length; i++) {
          const { path: relativePath, entry } = files[i];

          try {
            const blob = await entry.async('blob');

            // Supprimer le prefix du wrapper si détecté
            let cleanPath = relativePath;
            if (commonPrefix && relativePath.startsWith(commonPrefix)) {
              cleanPath = relativePath.slice(commonPrefix.length);
            }

            // Extraire le nom du fichier ET le chemin relatif
            const pathParts = cleanPath.split('/').filter(Boolean);
            const filename = pathParts[pathParts.length - 1];

            // Restaurer les extensions
            let finalFilename = filename;
            if (filename.endsWith('.bytes')) {
              // Détecter si c'était .glb ou .gltf (par défaut .glb)
              finalFilename = filename.replace('.bytes', '.glb');
            }

            const isLearnFile = pathParts.includes('learn');

            // Recalage des fichiers principaux pour qu'ils pointent sur le référentiel courant
            const isMainProcessJson = !isLearnFile && finalFilename === `${originalVariantName}.json`;

            if (isLearnFile && filename.toLowerCase().endsWith('.json')) {
              finalFilename = `${targetFolderName}_learn.json`;
            } else if (isMainProcessJson) {
              finalFilename = `${targetFolderName}.json`;
            } else if (finalFilename === `${originalVariantName}_learn.json`) {
              finalFilename = `${targetFolderName}_learn.json`;
            } else if (finalFilename.startsWith(`${originalVariantName}_`)) {
              finalFilename = `${targetFolderName}${finalFilename.slice(originalVariantName.length)}`;
            }

            // Le fichier JSON principal est renommé pour matcher targetFolderName, mais son
            // contenu garde le titre d'origine tel quel (process.processTitle) si on ne le
            // corrige pas ici : la variante affiche alors le titre d'une autre variante.
            let uploadBlob = blob;
            if (isMainProcessJson && targetFolderName !== originalVariantName) {
              try {
                const jsonData = JSON.parse(await blob.text());
                if (jsonData.process) {
                  jsonData.process.processTitle = targetFolderName;
                }
                uploadBlob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
              } catch (parseErr) {
                console.warn(`Impossible de mettre à jour processTitle pour ${finalFilename} :`, parseErr.message);
              }
            }

            const formData = new FormData();
            formData.append('file', uploadBlob, finalFilename);

            // Cet import cible directement targetFolderName (une seule variante) : le seul
            // sous-dossier légitime est "learn/". Tout autre segment restant après le retrait
            // du wrapper (ex: un niveau "nom de variante" ou "nom de référentiel" résiduel dans
            // le zip) ne doit PAS être conservé comme subPath, sinon il se retrouve dupliqué
            // sous targetFolderName (ex: "Référentiel 2/Référentiel 2/...").
            let subPath = '';

            // Si le fichier se termine par _learn.json ou s'il était déjà dans un dossier learn
            if (finalFilename.endsWith('_learn.json') || isLearnFile) {
              subPath = 'learn';
            }

            if (subPath) {
              formData.append('subPath', subPath);
            }

            const response = await fetch(
              `/api/data/${encodeURIComponent(targetChapterName)}/${encodeURIComponent(targetFolderName)}/upload-single`,
              {
                method: 'POST',
                body: formData,
                credentials: 'include'
              }
            );

            if (!response.ok) {
              const err = await response.json().catch(() => ({}));
              throw new Error(err.error || `Status ${response.status}`);
            }

            successCount++;
            console.log(`✓ ${i + 1}/${files.length} - ${relativePath}`);

          } catch (err) {
            errorCount++;
            console.error(`✗ Erreur pour ${relativePath}:`, err.message);
          }
        }

        alert(`Import terminé !\nNom d'origine détecté : "${originalVariantName}"\nRenommé en : "${targetFolderName}"\n✓ ${successCount} fichier(s) importé(s) dans ${targetChapterName}/${targetFolderName}\n✗ ${errorCount} échec(s)`);

        // On rafraîchit la liste des variantes du référentiel courant.
        try {
          await fetchLanguageFolders();
        } catch (e) {
          console.warn('Impossible de rafraîchir la liste des variantes après import :', e.message);
        }

      } catch (err) {
        console.error('Erreur lors de l\'import :', err);
        alert('Erreur lors de l\'import : ' + err.message);
      }
    };

    input.click();
  };

  const dispatch = useDispatch();

  // Nouvelle fonction pour charger le JSON et naviguer
  const handleSelectFolder = async (folder) => {
    try {
      console.log("test edit 2" + folder);
      const response = await fetch(`/api/data/${encodeURIComponent(chapterName)}/${encodeURIComponent(folder)}/${encodeURIComponent(folder)}.json`, { credentials: 'include' });
      if (!response.ok) throw new Error('Erreur lors du chargement du JSON');
      const data = await response.json();
      dispatch(updateJsonData(data));
      if (isFromVariant) {
        setChoiceFolder(folder);
        setChoiceModalOpen(true);
      } else {
        navigate(`/homemono/${chapterName}/${folder}.json`);
      }
    } catch (err) {
      alert('Impossible de charger ce JSON : ' + err.message);
    }
  };
  const [languageFolders, setLanguageFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderSuffix, setNewFolderSuffix] = useState("");
  const [createFolderError, setCreateFolderError] = useState(null);
  const [deleteFolderError, setDeleteFolderError] = useState(null);
  const [downloadError, setDownloadError] = useState(null);

  // États pour les détails de création de la variante
  const [newFolderTitle, setNewFolderTitle] = useState("");
  const [newFolderDescription, setNewFolderDescription] = useState("");
  const [newFolderHologramFile, setNewFolderHologramFile] = useState(null);
  const [hologramDeleted, setHologramDeleted] = useState(false);

  // États de la zone de travail pour la variante
  const [newFolderWorkspaceShape, setNewFolderWorkspaceShape] = useState("");
  const [newFolderWorkspaceSizeX, setNewFolderWorkspaceSizeX] = useState("");
  const [newFolderWorkspaceSizeY, setNewFolderWorkspaceSizeY] = useState("");
  const [newFolderWorkspaceSizeZ, setNewFolderWorkspaceSizeZ] = useState("");
  const [newFolderWorkspaceOffsetPosX, setNewFolderWorkspaceOffsetPosX] = useState("");
  const [newFolderWorkspaceOffsetPosY, setNewFolderWorkspaceOffsetPosY] = useState("");
  const [newFolderWorkspaceOffsetPosZ, setNewFolderWorkspaceOffsetPosZ] = useState("");
  const [newFolderWorkspaceOffsetRotX, setNewFolderWorkspaceOffsetRotX] = useState("0");

  const [showOffsetInputs, setShowOffsetInputs] = useState(false);

  // États pour l'édition de la variante
  const [isEditingFolder, setIsEditingFolder] = useState(false);
  const [editingFolder, setEditingFolder] = useState("");

  // Pour la duplication :
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateBaseFolder, setDuplicateBaseFolder] = useState("");
  const [duplicateSuffix, setDuplicateSuffix] = useState("");
  const [duplicateError, setDuplicateError] = useState("");

  // Pour la saisie du nom de variante en cas de conflit à l'import ZIP (même UI que
  // la duplication, à la place d'un window.prompt) :
  const [importNameDialogOpen, setImportNameDialogOpen] = useState(false);
  const [importNameValue, setImportNameValue] = useState("");
  const [importNameError, setImportNameError] = useState("");
  const importNameResolverRef = useRef(null);

  // Pour le choix d'éditeur (WORK ou LEARN) :
  const [choiceModalOpen, setChoiceModalOpen] = useState(false);
  const [choiceFolder, setChoiceFolder] = useState("");

  const navigate = useNavigate();
  const { chapterName } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isFromVariant = searchParams.get("from") === "variant";

  const fetchLanguageFolders = useCallback(async () => {
    try {
      const response = await fetch(`/api/data/${chapterName}/list-language-folders`, { credentials: 'include' });
      if (!response.ok) throw new Error(response.statusText);
      const data = await response.json();
      setLanguageFolders(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError(err);
      setLoading(false);
    }
  }, [chapterName]);

  useEffect(() => {
    if (chapterName) {
      fetchLanguageFolders();
    }
  }, [chapterName, fetchLanguageFolders]);

  //const handleGoBack = () => navigate(-1);
  const handleGoBack = () => navigate(isFromVariant ? `/ixivariant` : `/chapterManage`);

  const handleCreateFolderClick = () => {
    setIsCreatingFolder(true);
    setIsEditingFolder(false);
    setEditingFolder("");
    setNewFolderSuffix("");
    setCreateFolderError(null);
    setNewFolderTitle("");
    setNewFolderDescription("");
    setNewFolderHologramFile(null);
    setHologramDeleted(false);
    setNewFolderWorkspaceShape("");
    setNewFolderWorkspaceSizeX("");
    setNewFolderWorkspaceSizeY("");
    setNewFolderWorkspaceSizeZ("");
    setNewFolderWorkspaceOffsetPosX("");
    setNewFolderWorkspaceOffsetPosY("");
    setNewFolderWorkspaceOffsetPosZ("");
    setNewFolderWorkspaceOffsetRotX("0");
    setShowOffsetInputs(false);
  };

  const handleNewFolderSuffixChange = (e) => {
    setNewFolderSuffix(e.target.value);
  };

  const handleNewFolderTitleChange = (e) => {
    // Normaliser en NFC dès la saisie pour éviter les écarts entre formes Unicode
    // composée/décomposée (ex: "é" saisi différemment selon le clavier/OS).
    const value = e.target.value.normalize('NFC');
    // Autoriser les lettres accentuées et les chiffres, mais aucun caractère spécial.
    if (/^[\p{L}\p{N} ]*$/u.test(value)) {
      setNewFolderTitle(value);
    }
  };

  const handleCancelCreateFolder = () => {
    setIsCreatingFolder(false);
    setIsEditingFolder(false);
    setEditingFolder("");
    setNewFolderSuffix("");
    setCreateFolderError(null);
    setNewFolderTitle("");
    setNewFolderDescription("");
    setNewFolderHologramFile(null);
    setHologramDeleted(false);
    setNewFolderWorkspaceShape("");
    setNewFolderWorkspaceSizeX("");
    setNewFolderWorkspaceSizeY("");
    setNewFolderWorkspaceSizeZ("");
    setNewFolderWorkspaceOffsetPosX("");
    setNewFolderWorkspaceOffsetPosY("");
    setNewFolderWorkspaceOffsetPosZ("");
    setNewFolderWorkspaceOffsetRotX("0");
    setShowOffsetInputs(false);
  };

  const handleEditFolderClick = async (folder) => {
    try {
      console.log("test edit " + folder);
      const response = await fetch(`/api/data/${encodeURIComponent(chapterName)}/${encodeURIComponent(folder)}/${encodeURIComponent(folder)}.json`, { credentials: 'include' });
      if (!response.ok) throw new Error('Erreur lors du chargement du JSON');
      const data = await response.json();
      
      setEditingFolder(folder);
      setIsEditingFolder(true);
      setIsCreatingFolder(false);
      const currentSuffix = data.settings?.langCode || "";
      setNewFolderSuffix(currentSuffix);
      // Extraction des valeurs pour remplir les states de formulaire
      setNewFolderTitle(data.process?.processTitle || "");
      setNewFolderDescription(data.process?.processDesc || "");
      setNewFolderHologramFile(data.process?.mainHologram || "");
      setNewFolderWorkspaceShape(data.process?.workspaceShape || "");
      setNewFolderWorkspaceSizeX(data.process?.workspaceShapeSize?.x || "");
      setNewFolderWorkspaceSizeY(data.process?.workspaceShapeSize?.y || "");
      setNewFolderWorkspaceSizeZ(data.process?.workspaceShapeSize?.z || "");
      setNewFolderWorkspaceOffsetPosX(data.process?.workspaceOffsetPos?.x || "");
      setNewFolderWorkspaceOffsetPosY(data.process?.workspaceOffsetPos?.y || "");
      setNewFolderWorkspaceOffsetPosZ(data.process?.workspaceOffsetPos?.z || "");
      setNewFolderWorkspaceOffsetRotX(data.process?.workspaceOffsetRot?.x || "0");
      
      if (data.process?.workspaceOffsetPos?.x || data.process?.workspaceOffsetPos?.z) {
        setShowOffsetInputs(true);
      } else {
        setShowOffsetInputs(false);
      }
    } catch (err) {
      alert('Impossible de charger les informations de la variante : ' + err.message);
    }
  };

  const handleSaveEditFolder = async () => {
    try {
      // Charger le JSON existant pour préserver les étapes, settings, etc.
      const response = await fetch(`/api/data/${encodeURIComponent(chapterName)}/${encodeURIComponent(editingFolder)}/${encodeURIComponent(editingFolder)}.json`, { credentials: 'include' });
      if (!response.ok) throw new Error('Erreur lors du chargement du JSON');
      const existingData = await response.json();

      const targetFolderName = newFolderTitle.trim().normalize('NFC');
      let actualFolder = editingFolder;

      if (targetFolderName !== editingFolder) {
        // Appeler la route de renommage sur le serveur
        const renameRes = await fetch(`/api/data/rename-language-folder`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            chapterName,
            oldFolderName: editingFolder,
            newFolderName: targetFolderName
          })
        });
        if (!renameRes.ok) {
          const errData = await renameRes.json().catch(() => ({}));
          throw new Error(errData.error || `Erreur de renommage sur le serveur (status ${renameRes.status})`);
        }
        actualFolder = targetFolderName;
      }

      // Téléverser le nouvel hologramme principal si choisi
      let hologramFileName = existingData.process?.mainHologram || "";
      if(hologramDeleted){
        hologramFileName = "";
      } else if (newFolderHologramFile instanceof File) {
        await uploadFile({
          file: newFolderHologramFile,
          params: [],
          folder: `${chapterName}/${actualFolder}`,
          suffix: "Hologram",
          defaultExt: ".glb"
        });
        hologramFileName = 'mainHologram.glb';
      }

      const updatedJson = {
        ...existingData,
        settings: {
          ...existingData.settings,
          chapter: chapterName,
          langCode: newFolderSuffix.trim().toUpperCase()
        },
        process: {
          ...existingData.process,
          processTitle: newFolderTitle.trim(),
          processDesc: newFolderDescription.trim(),
          workspaceShape: newFolderWorkspaceShape,
          workspaceShapeSize: { 
            x: parseFloat(newFolderWorkspaceSizeX) || 0, 
            y: parseFloat(newFolderWorkspaceSizeY) || 0,
            z: parseFloat(newFolderWorkspaceSizeZ) || 0
          },
          workspaceOffsetPos: { 
            x: parseFloat(newFolderWorkspaceOffsetPosX) || 0, 
            y: parseFloat(newFolderWorkspaceOffsetPosY) || 0, 
            z: parseFloat(newFolderWorkspaceOffsetPosZ) || 0 
          },
          workspaceOffsetRot: { 
            x: parseFloat(newFolderWorkspaceOffsetRotX) || 0, 
            y: 0, 
            z: 0 
          },
          mainHologram: hologramFileName
        }
      };

      // Sauvegarder le fichier JSON avec le nom exact
      const saveResponse = await fetch(`/api/data/${encodeURIComponent(chapterName)}/${encodeURIComponent(actualFolder)}/${encodeURIComponent(actualFolder)}.json`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: 'include',
        body: JSON.stringify(updatedJson)
      });

      if (!saveResponse.ok) throw new Error(`Erreur lors de l'enregistrement du JSON : ${await saveResponse.text()}`);

      setIsEditingFolder(false);
      setEditingFolder("");
      // Réinitialiser les états
      handleCancelCreateFolder();
      fetchLanguageFolders();
      alert('Variante enregistrée avec succès !');
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la sauvegarde : ' + err.message);
    }
  };

  const handleChoiceSelect = (editorType) => {
    setChoiceModalOpen(false);
    if (editorType === "work") {
      navigate(`/homemono/${chapterName}/${choiceFolder}.json?from=variant`);
    } else if (editorType === "learn") {
      navigate(`/homemonoLearn/${chapterName}/${choiceFolder}?from=variant`);
    }
  };


  const handleCreateFolder = async () => {
    if (!newFolderTitle.trim()) {
      setCreateFolderError("Le titre est requis.");
      return;
    }
    // Utiliser le titre saisi à la place de chapterName pour le nom de variante
    const folderName = newFolderTitle.trim().normalize('NFC');
    const jsonFilename = `${folderName}.json`;
    const fullFolderName = folderName;

    try {
      // Créer le dossier (nom exact, pas de sanitize)
      const response = await fetch("/api/data/create-folder", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: 'include',
        body: JSON.stringify({ folderName: `${chapterName}/${fullFolderName}` }),
      });

      if (!response.ok) throw new Error(`Erreur lors de la création du dossier: ${await response.text()}`);

      // Téléverser l'hologramme principal si spécifié
      let hologramFileName = "";
      if (newFolderHologramFile) {
        await uploadFile({
          file: newFolderHologramFile,
          params: [],
          folder: `${chapterName}/${fullFolderName}`,
          suffix: "Hologram",
          defaultExt: ".glb"
        });
        hologramFileName = 'mainHologram.glb';
      }

      // Créer le fichier JSON initial avec la structure Redux
      const initialJson = {
        usage: "",
        settings: {
          contentVersion: 0,
          ownerName: "",
          ownerIdentifier: "",
          appVersion: "",
          chapter: chapterName,
          calibrateAfterStageID: 0,
          langCode: newFolderSuffix.trim().toUpperCase()
        },
        process: {
          processTitle: newFolderTitle.trim(),
          processDesc: newFolderDescription.trim(),
          workspaceShape: newFolderWorkspaceShape,
          workspaceShapeSize: { 
            x: parseFloat(newFolderWorkspaceSizeX) || 0, 
            y: parseFloat(newFolderWorkspaceSizeY) || 0,
            z: parseFloat(newFolderWorkspaceSizeZ) || 0
          },
          workspaceShapeRot: { x: 0, y: 0, z: 0 },
          workspaceOffsetPos: { 
            x: parseFloat(newFolderWorkspaceOffsetPosX) || 0, 
            y: parseFloat(newFolderWorkspaceOffsetPosY) || 0, 
            z: parseFloat(newFolderWorkspaceOffsetPosZ) || 0 
          },
          workspaceOffsetRot: { 
            x: parseFloat(newFolderWorkspaceOffsetRotX) || 0, 
            y: 0, 
            z: 0 
          },
          mainHologram: hologramFileName,
          // Étape pré-remplie : calibrage de la zone de travail
          steps: [
            {
              stepID: 1,
              stepTitle: "Calibrage de la zone de travail",
              stepDesc: "Pincez la zone de travail pour la déplacer à l'endroit désiré.\nUtilisez la poignée rouge pour l'orienter.",
              stepImg: "",
              stepHologram: "",
              stepHologramOffsetPos: { x: 0, y: 0, z: 0 },
              stepHologramOffsetRot: { x: 0, y: 0, z: 0 },
              stepHologramScale: "1",
              stepHologramInteractable: false,
              stepHologramCalibrate: false,
              stepAudioText: "Pincez la zone de travail pour la déplacer à l'endroit désiré.\nUtilisez la poignée rouge pour l'orienter.",
              stepAudio: "",
              stepNote: "",
              stepActions: []
            }
          ],
          stages: []
        }
      };

      // Sauvegarder le fichier JSON avec le nom exact
      const saveResponse = await fetch(`/api/data/${encodeURIComponent(chapterName)}/${encodeURIComponent(folderName)}/${encodeURIComponent(jsonFilename)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: 'include',
        body: JSON.stringify(initialJson)
      });

      if (!saveResponse.ok) throw new Error(`Erreur lors de la création du fichier JSON: ${await saveResponse.text()}`);

      // Mettre à jour la liste des dossiers
      setLanguageFolders((prev) => [...prev, fullFolderName]);
      setIsCreatingFolder(false);
      setNewFolderSuffix("");
      setNewFolderTitle("");
      setNewFolderDescription("");
      setNewFolderHologramFile(null);
      setHologramDeleted(false);
      setNewFolderWorkspaceShape("");
      setNewFolderWorkspaceSizeX("");
      setNewFolderWorkspaceSizeY("");
      setNewFolderWorkspaceSizeZ("");
      setNewFolderWorkspaceOffsetPosX("");
      setNewFolderWorkspaceOffsetPosY("");
      setNewFolderWorkspaceOffsetPosZ("");
      setNewFolderWorkspaceOffsetRotX("0");
      setShowOffsetInputs(false);

      // Initialiser le JSON dans Redux
      dispatch(updateJsonData(initialJson));

      // Rediriger vers HomeMonoPage avec le bon nom de fichier JSON
      if (!isFromVariant) {
        navigate(`/homemono/${encodeURIComponent(folderName)}/${encodeURIComponent(jsonFilename)}`);
      } else {
        setChoiceFolder(folderName);
        setChoiceModalOpen(true);
      }
    } catch (err) {
      console.error(err);
      setCreateFolderError(err.message);
    }
  };

  const handleDeleteFile = async (fieldName) => {
    if (!window.confirm("⚠️ Êtes-vous sûr de vouloir supprimer ce fichier ?\n\n" + "👉 " + fieldName)) return;
    setHologramDeleted(true);
    setNewFolderHologramFile("");
  };


  const handleDownloadHologram = async (fileName) => {
    try {
      const folderName = chapterName + "/" + editingFolder;
      const response = await axios.get(`/api/upload/download-hologram/${folderName}`, {
        params: { 
          fileName,
          t: new Date().getTime() }, // ✅ "fileName" correspond à req.query.fileName
        responseType: 'blob',
        withCredentials: true
      });

      const url = window.URL.createObjectURL(new Blob([response], { type: 'application/octet-stream' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || 'hologram.glb');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erreur lors du téléchargement du fichier.');
      console.error(err);
    }
  };

  const handleDeleteFolder = async (folder) => {
    // Confirmation avant suppression
    if (!window.confirm(`⚠️ A T T E N T I O N ⚠️
    Êtes-vous sûr de vouloir supprimer définitivement la variante "${folder}" ainsi que les formations qui en dépendent?`)) return;
    setDeleteFolderError(null);
    try {
      const res = await fetch(`/api/data/delete-language-folder/${chapterName}/${folder}`, { method: 'DELETE', credentials: 'include' });
      let errorMessage = `Status ${res.status}`;
      try {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const err = await res.json();
          errorMessage = err.error || errorMessage;
        } else {
          const text = await res.text();
          errorMessage = text.slice(0, 200); // Limit error text length
        }
      } catch (parseErr) {
        console.error('Erreur lors du parsing de la réponse :', parseErr);
      }

      if (!res.ok) {
        throw new Error(errorMessage);
      }

      //supp de la référence dans les contenus des casques de l'utilisateur
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const userEmail = authUser?.user_EMAIL;
      if (userEmail) {
        await fetch("/api/headset/cleanup-creator-contents", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ userEmail })
        });
        console.log(`Contenus de casque nettoyés pour ${userEmail}`);
      }

      fetchLanguageFolders();
    } catch (err) {
      console.error(err);
      setDeleteFolderError(err.message);
    }
  };

  // Ouvre la boîte de dialogue de duplication
  const openDuplicateDialog = (folder) => {
    setDuplicateBaseFolder(folder);
    setDuplicateSuffix("");
    setDuplicateError("");
    setDuplicateDialogOpen(true);
  };

  // Ferme la boîte de dialogue de duplication
  const closeDuplicateDialog = () => {
    setDuplicateDialogOpen(false);
    setDuplicateBaseFolder("");
    setDuplicateSuffix("");
    setDuplicateError("");
  };

  // Demande un nom de variante via la même UI que la duplication (au lieu d'un
  // window.prompt), utilisée quand l'import ZIP rencontre un nom de variante déjà pris.
  // Retourne une Promise résolue avec le nom choisi, ou null si l'utilisateur annule.
  const askImportVariantName = (suggestedName, message) => {
    return new Promise((resolve) => {
      setImportNameValue(suggestedName);
      setImportNameError(message || "");
      setImportNameDialogOpen(true);
      importNameResolverRef.current = resolve;
    });
  };

  const cancelImportNameDialog = () => {
    setImportNameDialogOpen(false);
    if (importNameResolverRef.current) {
      importNameResolverRef.current(null);
      importNameResolverRef.current = null;
    }
  };

  const confirmImportNameDialog = () => {
    const trimmed = importNameValue.trim().normalize('NFC');
    if (!trimmed) {
      setImportNameError("Veuillez saisir un nom valide pour la variante.");
      return;
    }
    if (/[\\/]/.test(trimmed)) {
      setImportNameError("Le nom ne peut pas contenir de caractère '/' ou '\\'.");
      return;
    }
    if (languageFolders.includes(trimmed)) {
      setImportNameError("Une variante avec ce nom existe déjà.");
      return;
    }
    setImportNameDialogOpen(false);
    if (importNameResolverRef.current) {
      importNameResolverRef.current(trimmed);
      importNameResolverRef.current = null;
    }
  };

  // Gère la duplication après saisie du nom de la nouvelle variante
  const confirmDuplicateFolder = async () => {
    setDownloadError(null);
    setDuplicateError("");
    const folder = duplicateBaseFolder;
    // Le nom saisi par l'utilisateur EST le nom final de la nouvelle variante
    // (on ne préfixe plus avec le nom de la variante d'origine).
    const newFolderName = duplicateSuffix.trim().normalize('NFC');
    if (!newFolderName) {
      setDuplicateError("Veuillez saisir un nom valide pour la nouvelle variante.");
      return;
    }
    if (/[\\/]/.test(newFolderName)) {
      setDuplicateError("Le nom ne peut pas contenir de caractère '/' ou '\\'.");
      return;
    }
    if (languageFolders.includes(newFolderName)) {
      setDuplicateError("Une variante avec ce nom existe déjà.");
      return;
    }
    try {
      const response = await fetch(`/api/data/duplicate-language-folder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ chapterName, folder, newFolderName })
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || `Status ${response.status}`);
      }
      fetchLanguageFolders();
      closeDuplicateDialog();
      alert(`Variante dupliquée sous le nom : ${newFolderName}`);
    } catch (err) {
      setDuplicateError('Erreur lors de la duplication : ' + err.message);
    }
  };

  if (loading) return <div>Chargement...</div>;
  if (error) return <div className="alert alert-danger">{error.message}</div>;



  // Fonction pour dupliquer une variante de langue
  const handleDuplicateFolder = async (folder) => {
    openDuplicateDialog(folder);
  };

  return (
    <div className="page-content">
      <div className="container mt-8">
        <div className="card shadow-sm p-4 position-relative">
          <h1 className='text-primary text-center'>iXiMAKER</h1>
          <h2 className="text-tertiary mb-2 text-center">{isFromVariant ? "Liste des Variantes" : "Procédure et variantes"}</h2>
          <h3 className="text-primary text-center">{chapterName}</h3>
          <p className="text-secondary mb-4 text-center"> ℹ️Sélectionnez une variante, ou{' '}
            <button
              className="text-primary p-0 m-0 bg-transparent border-0 cursor-pointer"
              onClick={handleCreateFolderClick}
            > créez-en un nouveau.
            </button>
          </p>
          {/* Boîte de dialogue de duplication */}
          {duplicateDialogOpen && (
            <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.2)' }} tabIndex="-1">
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title text-primary">Dupliquer la procédure</h5>
                    <button type="button" className="btn-close" aria-label="Fermer" onClick={closeDuplicateDialog}></button>
                  </div>
                  <div className="modal-body">
                    <p className="text-center text-secondary ">Vous allez créer une <b>nouvelle version</b><br />
                      Tous les contenus seront modifiables individuellement.</p>
                    <div className="mb-3">
                      <label className="form-label">Nom de la nouvelle variante</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Nom de la nouvelle variante ex: demo, fr, en..."
                        value={duplicateSuffix}
                        onChange={e => setDuplicateSuffix(e.target.value)}
                        autoFocus
                      />
                      {duplicateError && <div className="alert alert-danger mt-2">{duplicateError}</div>}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={closeDuplicateDialog}>Annuler</button>
                    <button type="button" className="btn btn-primary" onClick={confirmDuplicateFolder}>Dupliquer</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Boîte de dialogue de saisie du nom de variante en cas de conflit à l'import ZIP */}
          {importNameDialogOpen && (
            <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.2)' }} tabIndex="-1">
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title text-primary">Nom de la variante</h5>
                    <button type="button" className="btn-close" aria-label="Fermer" onClick={cancelImportNameDialog}></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Nom de la variante</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Nom de la variante ex: demo, fr, en..."
                        value={importNameValue}
                        onChange={e => setImportNameValue(e.target.value)}
                        autoFocus
                      />
                      {importNameError && <div className="alert alert-danger mt-2">{importNameError}</div>}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={cancelImportNameDialog}>Annuler</button>
                    <button type="button" className="btn btn-primary" onClick={confirmImportNameDialog}>Importer</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Boîte de dialogue de choix d'éditeur (WORK ou LEARN) */}
          {choiceModalOpen && (
            <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content shadow-lg border border-primary">
                  <div className="modal-header bg-light">
                    <h5 className="modal-title text-primary fw-bold">
                      <i className="bx bx-navigation me-2"></i>Accéder aux éditeurs
                    </h5>
                    <button type="button" className="btn-close" aria-label="Fermer" onClick={() => setChoiceModalOpen(false)}></button>
                  </div>
                  <div className="modal-body text-center p-4">
                    <p className="fs-5 text-secondary">
                      Choisissez l'éditeur pour la variante :
                    </p>
                    <h4 className="text-primary fw-bold mb-4">{choiceFolder}</h4>
                    
                    <div className="d-flex flex-column gap-3 mx-auto" style={{ maxWidth: '300px' }}>
                      <button 
                        type="button" 
                        className="btn btn-outline-primary btn-lg py-3 px-4 d-flex align-items-center justify-content-center" 
                        onClick={() => handleChoiceSelect("work")}
                      >
                        <i className="mdi mdi-book-outline fs-3 me-2"></i>
                        <span className="fw-bold">Procédures [WORK]</span>
                      </button>
                      
                      <button 
                        type="button" 
                        className="btn btn-outline-primary btn-lg py-3 px-4 d-flex align-items-center justify-content-center" 
                        onClick={() => handleChoiceSelect("learn")}
                      >
                        <i className="mdi mdi-school-outline fs-3 me-2"></i>
                        <span className="fw-bold">Évaluations [LEARN]</span>
                      </button>
                    </div>
                  </div>
                  <div className="modal-footer bg-light d-flex justify-content-center">
                    <button type="button" className="btn btn-secondary px-4 d-none" onClick={() => setChoiceModalOpen(false)}>Rester sur Variant</button>
                    <button type="button" className="btn btn-outline-secondary px-4" onClick={() => setChoiceModalOpen(false)}>Retour aux variantes</button>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="container mt-4">
            {languageFolders.length === 0 ? (
              <div className="alert alert-info">Aucune variante disponible. Commencez par créer une procédure dans iXiWORK.</div>
            ) : (
              <div className="row" alt="liste des variantes">
                {languageFolders.map((folder) => (
                  <div className="col-md-6 mb-1" key={folder}>
                    <div className="shadow-sm border rounded p-2 mb-2">
                      <div className="row">
                        <div className="col-10" alt="Titre de la variante">
                          <button
                            className="btn btn-link text-decoration-none text-primary p-0"
                            style={{ textAlign: 'left' }}
                            onClick={() => handleSelectFolder(folder)}
                          >
                             <span className="ms-2 fs-4"><i className='mdi mdi-source-branch me-2'></i>{folder}</span>
                          </button>
                        </div>
                        <div className="col-2 d-flex justify-content-end ms-auto" alt="boutons d'édition">
                          <button
                            className="btn btn-sm btn-outline-secondary me-2"
                            onClick={() => {
                              if (isFromVariant) {
                                handleEditFolderClick(folder);
                              } else {
                                handleSelectFolder(folder);
                              }
                            }}
                            title={isFromVariant ? "Modifier les informations de la variante" : "Éditer cette procédure"}
                          >
                            <FaEdit />
                          </button>

                          <button
                            className="btn btn-sm btn-outline-success me-2"
                            onClick={() => handleDownloadJson(folder)}
                            title="Télécharger le contenu de la procédure"
                          >
                            <FaUpload />
                          </button>

                          {isFromVariant && (
                            <>
                              <button
                                className="btn btn-sm btn-outline-primary me-2"
                                onClick={() => handleDuplicateFolder(folder)}
                                title="Dupliquer cette procédure pour créer 
                                  une nouvelle variante"
                              >
                                <FaRegCopy />
                              </button>

                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteFolder(folder)}
                                title="⚠️ATTENTION⚠️
                                  Supprimer DEFINITIVEMENT cette version
                                  et toutes les formations associées?"
                              >
                                <FaTrash />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
             <div className="col-md-12 d-flex justify-content-center" alt="retour/gestion des variantes">
                {!(isCreatingFolder || isEditingFolder) ? (
                  <>
                    <div className="col-md-7 mb-3 mt-4 d-flex justify-content-between">
                      <button className="btn btn-outline-secondary me-2" onClick={handleGoBack}>
                        <i className="mdi mdi-arrow-left-bold fs-3 align-middle"></i> Retour aux référentiels
                      </button>
                      {isFromVariant && (
                        <>
                          <button onClick={handleCreateFolderClick} className="btn btn-outline-primary"
                            title="Créer une nouvelle variante vierge">
                            <i className="mdi mdi-source-branch-plus fs-3 align-middle"></i>  Nouvelle variante vierge
                          </button>
                          <button onClick={handleImportZip} className="btn btn-outline-success ms-2"
                            title="Importer une variante depuis un fichier ZIP">
                            <i className="mdi mdi-download fs-3 align-middle"></i> Importer depuis ZIP
                          </button>
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  isFromVariant && (
                   <div className="col-md-12 mb-3 mt-4 card p-4 border border-primary">
                     <h3 className="text-primary mb-3 text-center">
                       {isEditingFolder ? `Modifier la variante : ${editingFolder}` : "Paramètres de la variante"}
                     </h3>
                     
                     <div className="row mb-3">
                        <div className="col-md-3" title="Nom de la variante, telle qu'elle sera affichée dans iXiWORK.">
                          <label className="form-label">Nom de la variante ℹ️</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Nom de la Variante"
                            value={newFolderTitle}
                            onChange={handleNewFolderTitleChange}
                          />
                        </div>

                        <div className="col-md-4" title="Description de la variante, telle qu'elle sera affichée dans iXiWORK">
                          <label className="form-label">Description ℹ️</label>
                          <textarea
                            className="form-control"
                            placeholder="Détail de la variante"
                            value={newFolderDescription}
                            onChange={e => setNewFolderDescription(e.target.value)}
                          />
                        </div>

                        <div className="col-md-2" title="Code pays de la variante au format ISO 3166 (ex: fr, en, de, cn...)">
                          <label className="form-label">Code pays ℹ️</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="ex: fr, en..."
                            value={newFolderSuffix}
                            onChange={handleNewFolderSuffixChange}
                          />
                        </div>
                       
                        <div className="col-md-3" title="Modèle 3D de l'hologramme initial au format .glb&#10;👉 Permet de visualiser l'objet en plus du tracé de la zone de travail.&#10;(Si vide, vous travaillerez un gabarit générique)">
                          <label className="form-label">Hologramme principal (glb) ℹ️</label>
                          {newFolderHologramFile ? (
                            <div alt="mainHologram" className="d-flex align-items-center">
                              <input alt="replace file"
                                type="file"
                                accept=".glb"
                                className="form-control"
                                style={{ width: "140px" }}
                                title='♻️ Remplacer le fichier existant'
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    setNewFolderHologramFile(file);
                                    setHologramDeleted(false);
                                  }
                                }}
                              />
                              <div alt="delete file">
                                {/*<span className="text-primary small fw-bold ms-2">[Fichier existant]</span>*/}
                                {/* 👇 BOUTON DOWNLOAD */}
                                {typeof newFolderHologramFile === "string" && (
                                  <button
                                  type="button"
                                  className="btn btn-sm btn-outline-success ms-2"
                                  onClick={() => handleDownloadHologram(newFolderHologramFile)}
                                  title={`⬇️ Télécharger le fichier\n(${newFolderHologramFile})`}
                                  >
                                    <FaDownload />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger ms-2"
                                  onClick={() => handleDeleteFile("mainHologram")}
                                  title={`❌ Supprimer le fichier existant\n(${newFolderHologramFile})`}
                                >
                                  <FaTrash />
                                </button>
                              </div>
                            </div>
                          ) : (
                          <input
                            type="file"
                            accept=".glb"
                            className="form-control"
                            onChange={e => {
                              const file = e.target.files[0];
                              if (file) {
                                setNewFolderHologramFile(file);
                                setHologramDeleted(false);
                              }
                            }}
                          />
                          )}
                        </div>
                      </div>

                    <div className="card shadow-sm border-primary py-4 mb-4" style={{ borderStyle: 'dashed', borderWidth: '1px' }}>
                      <h3 className="text-primary text-center">Zone de travail</h3>
                      <p className="col-md-6 text-center small mx-auto mb-4">
                        Réglage de la zone de travail (workspace).
                      </p>
                      
                      <div className="row px-3">
                        <div className="col-md-3" title="Forme de la zone de travail&#10;(Correpond généralement à la forme de base de l'hologramme principal)">
                          <label className="form-label"><i className="mdi mdi-image-filter-center-focus-strong" /> Forme de la zone ℹ️</label>
                          <select
                            className="form-select"
                            value={newFolderWorkspaceShape}
                            onChange={e => setNewFolderWorkspaceShape(e.target.value)}
                          >
                            <option value="">Sélectionner une forme</option>
                            <option value="square">Rectangulaire</option>
                            <option value="circle">Circulaire</option>
                          </select>
                        </div>

                        <div className="col-md-1" title="Largeur de la zone de travail en millimètres.&#10;(Correspond généralement à la largeur de l'hologramme)">
                          <label className="form-label">L.(mm)ℹ️</label>
                          <input
                            type="text"
                            placeholder="X"
                            className="form-control"
                            value={newFolderWorkspaceSizeX}
                            onChange={e => setNewFolderWorkspaceSizeX(e.target.value)}
                          />
                        </div>

                        <div className="col-md-1" title="Profondeur de la zone de travail en millimètres.&#10;(Correspond généralement à la profondeur de l'hologramme)">
                          <label className="form-label">
                            {newFolderWorkspaceOffsetRotX === "-90" ? "H.(mm)ℹ️" : "P.(mm)ℹ️"}
                          </label>
                          <input
                            type="text"
                            placeholder="Z"
                            className="form-control"
                            value={newFolderWorkspaceSizeZ}
                            onChange={e => setNewFolderWorkspaceSizeZ(e.target.value)}
                          />
                        </div>

                        {newFolderWorkspaceOffsetRotX !== "custom" && (
                          <div className="col-md-1" title="Hauteur de la zone de travail en millimètres.&#10;(Correspond généralement à la hauteur de l'hologramme)">
                            <label className="form-label">
                              {newFolderWorkspaceOffsetRotX === "-90" ? "P.(mm)ℹ️" : "H.(mm)ℹ️"}
                            </label>
                            <input
                              type="text"
                              placeholder="Y"
                              className="form-control"
                              value={newFolderWorkspaceSizeY}
                              onChange={e => setNewFolderWorkspaceSizeY(e.target.value)}
                            />
                          </div>
                        )}

                        <div className="col-md-3" title="Position de la zone de travail par rapport à l'hologramme.&#10;(Le calcul se fait en fonction de la hauteur, pour un modèle dont le centre est à l'origine)">
                          <label className="form-label">Position de la zone ℹ️</label>
                          <select
                            className="form-select"
                            value={newFolderWorkspaceOffsetPosY}
                            onChange={e => setNewFolderWorkspaceOffsetPosY(e.target.value)}
                          >
                            <option value={newFolderWorkspaceOffsetPosY}>Actuelle : ({newFolderWorkspaceOffsetPosY || "0"} mm)</option>
                            <option value={String(parseFloat(newFolderWorkspaceSizeY) / 2)}>Surface : ({String(parseFloat(newFolderWorkspaceSizeY) / 2 || "0")} mm)</option>
                            <option value="0">Milieu (0 mm)</option>
                            <option value={String(-(parseFloat(newFolderWorkspaceSizeY) / 2))}>Base : ({String(-(parseFloat(newFolderWorkspaceSizeY) / 2) || "0")} mm)</option>
                          </select>
                          
                          <div className="mt-3">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => setShowOffsetInputs(prev => !prev)}
                            >
                              {showOffsetInputs ? 'Masquer les décalages personnalisés' : 'Afficher les décalages personnalisés'}
                            </button>

                            {showOffsetInputs && (
                              <div className="mt-3">
                                <label className="form-label">Offset Pos L (mm)</label>
                                <input
                                  type="text"
                                  placeholder="Ex : 0"
                                  className="form-control mb-2"
                                  value={newFolderWorkspaceOffsetPosX}
                                  onChange={e => setNewFolderWorkspaceOffsetPosX(e.target.value)}
                                />
                                <label className="form-label">
                                  {newFolderWorkspaceOffsetRotX === "-90" ? "Offset Pos H (mm)" : "Offset Pos P (mm)"}
                                </label>
                                <input
                                  type="text"
                                  placeholder="Ex : 0"
                                  className="form-control"
                                  value={newFolderWorkspaceOffsetPosZ}
                                  onChange={e => setNewFolderWorkspaceOffsetPosZ(e.target.value)}
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-md-3" title="Rotation de la zone de travail par rapport à l'hologramme.">
                          <label className="form-label">Orientation ℹ️</label>
                          <select
                            className="form-select"
                            value={newFolderWorkspaceOffsetRotX}
                            onChange={e => setNewFolderWorkspaceOffsetRotX(e.target.value)}
                          >
                            <option value="0">Horizontale</option>
                            <option value="-90">Verticale</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {createFolderError && <div className="alert alert-danger">{createFolderError}</div>}
                    
                     <div className="d-flex justify-content-center gap-3">
                       <button onClick={handleCancelCreateFolder} className="btn btn-outline-secondary px-4">Annuler</button>
                       <button onClick={isEditingFolder ? handleSaveEditFolder : handleCreateFolder} className="btn btn-outline-primary px-4">
                         {isEditingFolder ? "Enregistrer" : "Créer"}
                       </button>
                     </div>
                  </div>
                 )
               )}
              {deleteFolderError && <div className="alert alert-danger mt-2">{deleteFolderError}</div>}

            </div>
            {downloadError && <div className="alert alert-danger mt-2">{downloadError}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};



export default LanguageManageWork;


//Creators : Florian Cohen-Joly @3DDGAMES -- Under license

